import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SearchType } from '../../Interfaces/ISearchType';
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  Lock,
  SwapHoriz,
} from '@mui/icons-material';
import {
  DataGrid,
  deDE,
  GridColDef,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import {
  IPostcode,
  usePostcodeList,
} from '../../hooks/reactQuerys/PostcodeAdminQueries';
import { PostcodeEditDialog, PostcodeEditMode } from './PostcodeEditDialog';
import { PostcodeMigrateDialog } from './PostcodeMigrateDialog';

// Notfall-Fallback-PLZ: darf nicht bearbeitet/gelöscht/ausgewählt werden.
const PROTECTED_POSTCODE = '00000';
const isProtected = (row: IPostcode) =>
  (row.Postcode ?? '').trim() === PROTECTED_POSTCODE;

/**
 * Verwaltung von Postleitzahlen (unter Einstellungen). Serverseitige Suche +
 * Pagination, Mehrfachauswahl mit Massenaktionen (Migrieren/Löschen) sowie
 * Einzelaktionen (Bearbeiten/Kopieren/Löschen). Nur für Administratoren.
 */
export const PostcodeAdminOverview: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState<SearchType>(SearchType.INCLUDES);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Suche entprellen und Seite zurücksetzen.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { postcodeResult, isFetchingPostcodes, refetch } = usePostcodeList(
    search,
    page,
    pageSize,
    searchType
  );
  const rows = postcodeResult?.rows ?? [];
  const total = postcodeResult?.total ?? 0;

  // Auswahl über Seiten hinweg: ausgewählte Zeilenobjekte merken.
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  const [selectedMap, setSelectedMap] = useState<Record<number, IPostcode>>({});

  const handleSelection = (model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setSelectedMap(prev => {
      const next: Record<number, IPostcode> = {};
      model.forEach(id => {
        const numId = Number(id);
        const found = rows.find(r => r.idPostcode === numId);
        next[numId] = found ?? prev[numId];
      });
      return next;
    });
  };

  const selectedRows = useMemo(
    () =>
      selectionModel
        .map(id => selectedMap[Number(id)])
        .filter((r): r is IPostcode => Boolean(r)),
    [selectionModel, selectedMap]
  );

  // Dialog-Steuerung
  const [editState, setEditState] = useState<{
    open: boolean;
    mode: PostcodeEditMode;
    initial: IPostcode | null;
  }>({ open: false, mode: 'create', initial: null });

  const [migrateState, setMigrateState] = useState<{
    open: boolean;
    mode: 'migrate' | 'delete';
    sources: IPostcode[];
  }>({ open: false, mode: 'migrate', sources: [] });

  const openEdit = (mode: PostcodeEditMode, initial: IPostcode | null) =>
    setEditState({ open: true, mode, initial });

  const openMigrate = (mode: 'migrate' | 'delete', sources: IPostcode[]) =>
    setMigrateState({ open: true, mode, sources });

  const clearSelection = () => {
    setSelectionModel([]);
    setSelectedMap({});
  };

  const columns: GridColDef[] = [
    { field: 'Postcode', headerName: 'Postleitzahl', flex: 1, minWidth: 120 },
    { field: 'City', headerName: 'Ort', flex: 2, minWidth: 160 },
    {
      field: 'FederalStateName',
      headerName: 'Bundesland',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      sortable: false,
      filterable: false,
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: params => {
        const row = params.row as IPostcode;
        if (isProtected(row)) {
          // Notfall-Fallback: nur Kopieren erlaubt, kein Bearbeiten/Löschen.
          return (
            <Box>
              <Tooltip title='Kopieren'>
                <IconButton size='small' onClick={() => openEdit('copy', row)}>
                  <ContentCopy fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Notfall-Fallback – gesperrt (nicht bearbeitbar/löschbar)'>
                <span>
                  <IconButton size='small' disabled>
                    <Lock fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box>
            <Tooltip title='Bearbeiten'>
              <IconButton size='small' onClick={() => openEdit('edit', row)}>
                <Edit fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Kopieren'>
              <IconButton size='small' onClick={() => openEdit('copy', row)}>
                <ContentCopy fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Löschen'>
              <IconButton
                size='small'
                color='error'
                onClick={() => openMigrate('delete', [row])}
              >
                <Delete fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <Typography variant='h5' sx={{ flexGrow: 1 }}>
          Postleitzahlen
        </Typography>
        <TextField
          size='small'
          select
          label='Suchbedingung'
          value={searchType}
          onChange={e => {
            setSearchType(e.target.value as SearchType);
            setPage(0);
          }}
          sx={{ minWidth: 210 }}
        >
          <MenuItem value={SearchType.STARTS_WITH}>
            Beginnt mit Sucheingabe
          </MenuItem>
          <MenuItem value={SearchType.INCLUDES}>Enthält Sucheingabe</MenuItem>
          <MenuItem value={SearchType.EXACT_MATCH}>
            Identisch mit Sucheingabe
          </MenuItem>
        </TextField>
        <TextField
          size='small'
          label='Suche (Postleitzahl oder Ort)'
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Tooltip title='Neue Postleitzahl'>
          <Button
            variant='contained'
            startIcon={<Add />}
            onClick={() => openEdit('create', null)}
          >
            Neu
          </Button>
        </Tooltip>
      </Stack>

      {selectedRows.length > 0 && (
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{ mb: 1 }}
        >
          <Typography variant='body2'>
            {selectedRows.length} ausgewählt
          </Typography>
          <Button
            size='small'
            startIcon={<SwapHoriz />}
            onClick={() => openMigrate('migrate', selectedRows)}
          >
            Migrieren
          </Button>
          <Button
            size='small'
            color='error'
            startIcon={<Delete />}
            onClick={() => openMigrate('delete', selectedRows)}
          >
            Löschen
          </Button>
        </Stack>
      )}

      <Box sx={{ height: '65vh', width: '100%' }}>
        <DataGrid
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={rows}
          columns={columns}
          getRowId={row => row.idPostcode}
          loading={isFetchingPostcodes}
          checkboxSelection
          isRowSelectable={params => !isProtected(params.row as IPostcode)}
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleSelection}
          paginationMode='server'
          rowCount={total}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={model => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>

      <PostcodeEditDialog
        open={editState.open}
        mode={editState.mode}
        initial={editState.initial}
        onClose={() => setEditState(s => ({ ...s, open: false }))}
        onSaved={() => refetch()}
      />

      <PostcodeMigrateDialog
        open={migrateState.open}
        mode={migrateState.mode}
        sources={migrateState.sources}
        onClose={() => setMigrateState(s => ({ ...s, open: false }))}
        onDone={() => {
          clearSelection();
          refetch();
        }}
      />
    </Box>
  );
};
